import api from '../axios';
import type { PlanTier, SubscriptionStatus } from '~/store/entitlements/interfaces';

export type BillingInterval = 'day' | 'week' | 'month' | 'year';
export type InvoiceStatus =
  | 'pending'
  | 'paid'
  | 'overdue'
  | 'refunded'
  | 'failed'
  | 'cancelled';

export interface BillingOffer {
  slug: string;
  label: string;
  interval: BillingInterval;
  intervalCount: number;
  commitmentMonths: number;
  amountCents: number;
  compareAtCents: number | null;
  currency: string;
  isDefault: boolean;
  maxInstallments: number;
  installmentInterestEnabled: boolean;
}

export interface InstallmentOption {
  count: number;
  perInstallmentCents: number;
  totalCents: number;
  hasInterest: boolean;
}

export interface InstallmentsPreview {
  offerSlug: string;
  baseCents: number;
  contractedCents: number;
  coupon: { code: string; discountCents: number; finalAmountCents: number } | null;
  options: InstallmentOption[];
}

export interface BillingPlan {
  slug: string;
  name: string;
  tier: PlanTier;
  description: string | null;
  trialDays: number;
  benefits: string[] | null;
  autoRenewDefault: boolean;
  offers: BillingOffer[];
}

export interface BillingInvoice {
  id: string;
  amountCents: number;
  status: InvoiceStatus;
  dueDate: string | null;
  paidAt: string | null;
  invoiceUrl: string | null;
}

export interface RenewalPreview {
  chargeAt: string;
  totalCents: number;
  installmentCount: number;
  perInstallmentCents: number;
}

export interface MyBilling {
  subscription: {
    status: SubscriptionStatus;
    planSlug: string;
    offer: BillingOffer | null;
    amountCents: number | null;
    creditBalanceCents: number;
    autoRenew: boolean;
    cancelAtPeriodEnd: boolean;
    renewsAt: string | null;
    currentPeriodEndsAt: string | null;
    commitmentEndsAt: string | null;
    trialEndsAt: string | null;
    cancelledAt: string | null;
    renewalPreview: RenewalPreview | null;
  } | null;
  invoices: BillingInvoice[];
}

export interface CardInput {
  token?: string;
  holderName?: string;
  number?: string;
  expiryMonth?: string;
  expiryYear?: string;
  ccv?: string;
  holder?: {
    name: string;
    email: string;
    cpfCnpj: string;
    postalCode: string;
    addressNumber: string;
    phone?: string;
  };
}

export interface CheckoutResult {
  offer: BillingOffer;
  coupon: {
    code: string;
    discountCents: number;
    finalAmountCents: number;
  } | null;
  installments?: {
    count: number;
    totalCents: number;
    perInstallmentCents: number;
    hasInterest: boolean;
  };
  status: 'active' | 'pending_payment';
  invoice: {
    id: string;
    amountCents: number;
    invoiceUrl: string | null;
  } | null;
}

export interface ChangePlanResult {
  kind: 'upgrade' | 'downgrade' | 'annual_contracted';
  from: BillingOffer;
  to: BillingOffer;
  proratedChargeCents: number;
  creditUsedCents: number;
  creditAddedCents: number;
  creditBalanceCents: number;
  commitmentEndsAt: string | null;
  installments?: {
    count: number;
    totalCents: number;
    perInstallmentCents: number;
    hasInterest: boolean;
  };
  status: SubscriptionStatus;
}

export type CouponType = 'percentage' | 'fixed_amount';

export type CouponPreview =
  | {
      code: string;
      discountKind: 'amount';
      couponType: CouponType;
      value: number;
      regularAmountCents: number;
      finalAmountCents: number;
      discountCents: number;
    }
  | {
      code: string;
      discountKind: 'installments';
      couponType: CouponType;
      value: number;
      firstNInstallments: number;
      installmentAmountCents: number;
      regularAmountCents: number;
      discountCents: number;
    };

export function getBillingOffers() {
  return api.get<{ plans: BillingPlan[] }>('/v1/billing/offers');
}

export function validateCoupon(offerSlug: string, code: string) {
  return api.post<CouponPreview>('/v1/billing/coupons/validate', { offerSlug, code });
}

export function getInstallmentOptions(offerSlug: string, couponCode?: string) {
  return api.post<InstallmentsPreview>('/v1/billing/installments', {
    offerSlug,
    ...(couponCode ? { couponCode } : {}),
  });
}

export function getMyBilling() {
  return api.get<MyBilling>('/v1/billing/me', {
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    params: { t: Date.now() },
  });
}

export function checkout(
  offerSlug: string,
  card: CardInput,
  couponCode?: string,
  installments?: number,
  idempotencyKey?: string,
) {
  return api.post<CheckoutResult>(
    '/v1/billing/checkout',
    {
      offerSlug,
      card,
      ...(couponCode ? { couponCode } : {}),
      ...(installments ? { installments } : {}),
    },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
  );
}

export function changePlan(
  offerSlug: string,
  card?: CardInput,
  installments?: number,
  idempotencyKey?: string,
) {
  return api.post<ChangePlanResult>(
    '/v1/billing/change-plan',
    {
      offerSlug,
      ...(card ? { card } : {}),
      ...(installments ? { installments } : {}),
    },
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
  );
}

export function reactivateSubscription(idempotencyKey?: string) {
  return api.post<{ status: SubscriptionStatus; cancelAtPeriodEnd: boolean }>(
    '/v1/billing/reactivate',
    undefined,
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined,
  );
}

export function cancelSubscription() {
  return api.post<{
    status: SubscriptionStatus;
    cancelAtPeriodEnd?: boolean;
    effectiveAt?: string | null;
  }>('/v1/billing/cancel');
}
